import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl } from '@angular/forms';
import { BasketApplianceItemQuoteRequest } from '@basket';
import { FormOption } from '@forms';
import { format, getMonth, getYear, setMonth } from 'date-fns';
import { distinctUntilChanged, startWith } from 'rxjs';
import { SelectedApplianceDetails } from '../models/selected-appliance-details.interface';

export function createMonthOptionsForYear(
  selectedYear?: number,
): FormOption<number>[] {
  const now = new Date();
  const currentYear = getYear(now);
  const currentMonth = getMonth(now) + 1; // 1..12

  let start = 1;
  let end = 12;

  if (selectedYear === currentYear) {
    // Current year: Jan..currentMonth
    start = 1;
    end = currentMonth;
  } else if (selectedYear === currentYear - 1) {
    // Previous year: currentMonth..Dec
    start = currentMonth;
    end = 12;
  } else {
    // Any earlier year: full year
    start = 1;
    end = 12;
  }

  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => {
    const month = start + idx; // 1..12
    return {
      value: month,
      label: format(setMonth(new Date(2000, 0, 1), month - 1), 'LLLL'),
    };
  });
}

/** Keeps purchaseMonth within the valid range for the selected purchaseYear.
 *  - current year:        Jan..currentMonth
 *  - previous year:       currentMonth..Dec
 *  - older years:         Jan..Dec
 */
export function adjustMonthForYearSelection(
  purchaseYearControl: AbstractControl | null,
  purchaseMonthControl: AbstractControl | null,
  destroyRef: DestroyRef,
): void {
  const yearControl = purchaseYearControl as FormControl<
    number | string | null
  > | null;
  const monthControl = purchaseMonthControl as FormControl<
    number | null
  > | null;
  if (!yearControl || !monthControl) return;

  const now = new Date();
  const CURRENT_YEAR = getYear(now);
  const CURRENT_MONTH = getMonth(now) + 1; // 1..12

  const allowedRange = (year: number): [min: number, max: number] => {
    if (year === CURRENT_YEAR) return [1, CURRENT_MONTH]; // Jan..current
    if (year === CURRENT_YEAR - 1) return [CURRENT_MONTH, 12]; // current..Dec
    return [1, 12]; // full year
  };

  const clamp = (v: number, min: number, max: number) =>
    Math.min(Math.max(v, min), max);

  const normalize = (rawYear: number | string | null) => {
    const year = coerceYear(rawYear);

    // 🚫 If year is not chosen yet, do nothing — avoid preselecting December.
    if (year == null) {
      return;
    }

    const [minMonth, maxMonth] = allowedRange(year);
    const value = monthControl.value; // number | null

    if (value == null) {
      // ✅ Prefill to a sensible boundary:
      // - current year  → current month (maxMonth)
      // - previous/older → start of range (minMonth)
      const prefill = year === CURRENT_YEAR ? maxMonth : minMonth;

      monthControl.setValue(prefill, { emitEvent: false });
    } else {
      // Clamp existing value if out of range
      const clamped = clamp(value, minMonth, maxMonth);
      if (clamped !== value) {
        monthControl.setValue(clamped, { emitEvent: false });
      }
    }

    monthControl.updateValueAndValidity({ emitEvent: false });
  };

  yearControl.valueChanges
    .pipe(
      // This fires once right away; with the guard above,
      // nothing happens until the user actually picks a year.
      startWith(yearControl.value),
      distinctUntilChanged(),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe(normalize);
}

/**
 * Coerces a year-like value to an integer year or null.
 * - number: must be a finite integer (e.g., 2025). Rejects NaN, Infinity, floats.
 * - string: must be exactly 4 digits (e.g., "2025", " 2025 "). Rejects everything else.
 * - other types: null.
 */
export function coerceYear(input: unknown): number | null {
  if (input == null) return null;

  if (typeof input === 'number') {
    return Number.isFinite(input) && Number.isInteger(input) ? input : null;
  }

  if (typeof input === 'string') {
    const t = input.trim();
    if (!/^\d{4}$/.test(t)) return null; // only 4 digits allowed
    return Number(t); // guaranteed finite integer
  }

  return null;
}

export function formatSelectedApplianceToBasketApplianceItemQuoteRequest(
  selectedAppliances: SelectedApplianceDetails[],
): BasketApplianceItemQuoteRequest[] {
  return selectedAppliances.map((appliance) => ({
    applianceCode: appliance.applianceCode,
    applianceName: appliance.applianceName,
    brandCode: appliance.manufacturer as string,
    brandName: appliance.brandName,
    purchaseMonth: Number(appliance.purchaseMonth),
    purchasePrice: Number(appliance.purchasePrice),
    purchaseYear: Number(appliance.purchaseYear),
    warranty: 12,
  }));
}
